import type { Notification } from "@storytell/client";

// The list and unread count move together on every user action and every
// incoming event; these helpers own that coupling. The paging cursor stays out
// — it lives in the service.
interface NotificationsState {
  list: Notification[];
  count: number;
}

// A reconnect can redeliver the boundary event, so skip a notification whose
// id we already hold instead of prepending a duplicate.
export function applyIncoming(list: Notification[], count: number, n: Notification): NotificationsState {
  if (list.some((existing) => existing.id === n.id)) {
    return { list, count };
  }
  return { list: [n, ...list], count: count + 1 };
}

export function applyMarkRead(list: Notification[], count: number, id: string): NotificationsState {
  const now = new Date().toISOString();
  let clearedUnread = 0;

  const next = list.map((n) => {
    if (n.id === id && n.readAt === null) {
      clearedUnread++;
      return { ...n, readAt: now };
    }
    return n;
  });

  return { list: next, count: Math.max(0, count - clearedUnread) };
}

export function applyMarkAllRead(list: Notification[]): NotificationsState {
  const now = new Date().toISOString();
  const next = list.map((n) => (n.readAt === null ? { ...n, readAt: now } : n));
  return { list: next, count: 0 };
}

export function applyDelete(list: Notification[], count: number, id: string): NotificationsState {
  const removed = list.find((n) => n.id === id);
  const wasUnread = removed?.readAt === null;
  return {
    list: list.filter((n) => n.id !== id),
    count: wasUnread ? Math.max(0, count - 1) : count,
  };
}
